import { useState, useEffect } from "react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Trash2, Plus, Upload, X, Image as ImageIcon } from "lucide-react";
import uploadService from "../../services/uploadService";

// Simple internal types
interface QuestionFormProps {
  initialData?: any;
  onSave: (question: any) => void;
  onCancel: () => void;
}

export function QuestionForm({
  initialData,
  onSave,
  onCancel,
}: QuestionFormProps) {
  const [question, setQuestion] = useState({
    questionText: "",
    questionType: "multiple-choice", // multiple-choice, true-false, multi-select, essay, short-answer
    marks: 1,
    options: [
      { text: "", isCorrect: false },
      { text: "", isCorrect: false },
      { text: "", isCorrect: false },
      { text: "", isCorrect: false },
    ],
    correctAnswer: "", // for true/false or text
    explanation: "",
    hints: [] as string[],
    tags: [] as string[],
    attachments: [] as { type: string; url: string; name: string }[],
  });
  const [uploading, setUploading] = useState(false);

  // Helper for tags input
  const [tagInput, setTagInput] = useState("");
  const [hintInput, setHintInput] = useState("");

  useEffect(() => {
    if (initialData) {
      setQuestion({
        ...initialData,
        questionText: initialData.questionText || initialData.text || "",
        questionType:
          initialData.questionType || initialData.type || "multiple-choice",
        hints: initialData.hints || [],
        tags: initialData.tags || [],
        options:
          initialData.options?.length > 0
            ? initialData.options
            : [
                { text: "", isCorrect: false },
                { text: "", isCorrect: false },
              ],
      });
    }
  }, [initialData]);

  const handleOptionChange = (index: number, field: string, value: any) => {
    const newOptions = [...question.options];
    newOptions[index] = { ...newOptions[index], [field]: value } as any;

    // If setting correct answer and single choice, unset others
    if (
      field === "isCorrect" &&
      value === true &&
      (question.questionType === "multiple-choice" ||
        question.questionType === "true-false")
    ) {
      newOptions.forEach((opt, i) => {
        if (i !== index) opt.isCorrect = false;
      });
    }

    setQuestion({ ...question, options: newOptions });
  };

  const addOption = () => {
    setQuestion({
      ...question,
      options: [...question.options, { text: "", isCorrect: false }],
    });
  };

  const removeOption = (index: number) => {
    setQuestion({
      ...question,
      options: question.options.filter((_, i) => i !== index),
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      let url: any;
      const isImage = file.type.startsWith("image");

      if (isImage) {
        url = await uploadService.uploadImage(file, "questions");
      } else {
        url = await uploadService.uploadAttachment(file);
      }

      const type = isImage ? "image" : "file";
      const attachment = {
        type,
        url: typeof url === "string" ? url : url.url, // Handle potential object response
        name: file.name,
      };

      setQuestion({
        ...question,
        attachments: [...question.attachments, attachment],
      });
    } catch (error) {
      console.error("Upload failed", error);
      alert("Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index: number) => {
    setQuestion({
      ...question,
      attachments: question.attachments.filter((_, i) => i !== index),
    });
  };

  // Tag Handlers
  const addTag = () => {
    if (tagInput.trim() && !question.tags.includes(tagInput.trim())) {
      setQuestion({ ...question, tags: [...question.tags, tagInput.trim()] });
      setTagInput("");
    }
  };
  const removeTag = (tagToRemove: string) => {
    setQuestion({
      ...question,
      tags: question.tags.filter((t) => t !== tagToRemove),
    });
  };

  // Hint Handlers
  const addHint = () => {
    if (hintInput.trim()) {
      setQuestion({
        ...question,
        hints: [...question.hints, hintInput.trim()],
      });
      setHintInput("");
    }
  };
  const removeHint = (idx: number) => {
    setQuestion({
      ...question,
      hints: question.hints.filter((_, i) => i !== idx),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Basic validation
    if (!question.questionText) return alert("Question text required");
    if (
      (question.questionType === "multiple-choice" ||
        question.questionType === "multi-select") &&
      !question.options.some((o) => o.isCorrect)
    )
      return alert("Select at least one correct option");

    onSave(question);
  };

  return (
    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm max-w-4xl mx-auto">
      <h2 className="text-xl font-bold mb-6">
        {initialData ? "Edit Question" : "Add Question"}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Question Type
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={question.questionType}
              onChange={(e) =>
                setQuestion({ ...question, questionType: e.target.value })
              }
            >
              <option value="multiple-choice">Multiple Choice</option>
              <option value="multi-select">Multi Select</option>
              <option value="true-false">True / False</option>
              <option value="short-answer">Short Answer</option>
              <option value="essay">Essay / Free Text</option>
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Points</label>
            <Input
              type="number"
              min="1"
              value={question.marks}
              onChange={(e) =>
                setQuestion({ ...question, marks: Number(e.target.value) })
              }
            />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">
            Question Text
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[100px]"
            value={question.questionText}
            onChange={(e) =>
              setQuestion({ ...question, questionText: e.target.value })
            }
            placeholder="Enter your question here..."
          />
        </div>

        {/* Attachments */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Attachments (Optional)
          </label>
          <div className="flex flex-wrap gap-4">
            {question.attachments.map((att, index) => (
              <div
                key={index}
                className="relative group border rounded-lg p-2 bg-gray-50 flex items-center gap-2"
              >
                {att.type === "image" ? (
                  <ImageIcon size={16} />
                ) : (
                  <Upload size={16} />
                )}
                <span className="text-sm truncate max-w-[150px]">
                  {att.name}
                </span>
                <button
                  type="button"
                  onClick={() => removeAttachment(index)}
                  className="p-1 hover:bg-gray-200 rounded-full text-gray-500"
                >
                  <X size={14} />
                </button>
              </div>
            ))}

            <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-600">
              <Upload size={16} />
              {uploading ? "Uploading..." : "Upload Image/File"}
              <input
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                disabled={uploading}
              />
            </label>
          </div>
        </div>

        {/* Options Logic */}
        {(question.questionType === "multiple-choice" ||
          question.questionType === "multi-select") && (
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">
              Answer Options
            </label>
            <div className="space-y-2">
              {question.options.map((option, index) => (
                <div key={index} className="flex items-center gap-3">
                  <input
                    type={
                      question.questionType === "multiple-choice"
                        ? "radio"
                        : "checkbox"
                    }
                    name="correct-option"
                    checked={option.isCorrect}
                    onChange={(e) =>
                      handleOptionChange(index, "isCorrect", e.target.checked)
                    }
                    className="w-4 h-4 text-indigo-600 cursor-pointer"
                  />
                  <Input
                    value={option.text}
                    onChange={(e) =>
                      handleOptionChange(index, "text", e.target.value)
                    }
                    placeholder={`Option ${index + 1}`}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addOption}
              className="mt-2"
            >
              <Plus size={16} className="mr-1" /> Add Option
            </Button>
          </div>
        )}

        {(question.questionType === "short-answer" ||
          question.questionType === "essay") && (
          <div className="space-y-2 p-4 bg-gray-50 rounded-lg border border-dashed">
            <p className="text-sm text-gray-600">
              For{" "}
              {question.questionType === "short-answer"
                ? "Short Answer"
                : "Essay"}{" "}
              questions, answers will be manually graded or matched against
              keywords.
            </p>
            {question.questionType === "short-answer" && (
              <Input
                placeholder="Model Answer / Keywords (Optional)"
                value={question.correctAnswer}
                onChange={(e) =>
                  setQuestion({ ...question, correctAnswer: e.target.value })
                }
              />
            )}
          </div>
        )}

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">
            Explanation (Optional)
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            rows={2}
            value={question.explanation}
            onChange={(e) =>
              setQuestion({ ...question, explanation: e.target.value })
            }
            placeholder="Explain the answer..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Hints</label>
            <div className="flex gap-2">
              <Input
                value={hintInput}
                onChange={(e) => setHintInput(e.target.value)}
                placeholder="Add a hint"
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addHint())
                }
              />
              <Button type="button" size="sm" onClick={addHint}>
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {question.hints.map((hint, idx) => (
                <span
                  key={idx}
                  className="bg-yellow-50 text-yellow-700 px-2 py-1 rounded text-xs flex items-center gap-1"
                >
                  {hint}
                  <button type="button" onClick={() => removeHint(idx)}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Tags</label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="e.g. Chapter 1, Algebra"
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addTag())
                }
              />
              <Button type="button" size="sm" onClick={addTag}>
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {question.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs flex items-center gap-1"
                >
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" className="bg-[#102040] text-white">
            Save Question
          </Button>
        </div>
      </form>
    </div>
  );
}
